import { MessageSquareShare, Search } from 'lucide-react';
import React, { useCallback, useEffect, useState } from "react";
import DataTablePagination from '../../DataTablePagination';
import Spacing from '../../Spacing';
import BulkMessageDataTable from '../../DataTable/BulkMessageDataTable';

import { fetchMessage, getSMSLimitData, retrySendBulkMessage } from "../../../services/message";
import { debounce, generateMessageTableData, getPractice, notify, numberWithCommas, userMessageTemplates } from "../../../helpers";
import { useDocumentTitle } from '../../../hooks/customDocumentTitleHook';
import MessageSendModal from '../../Modal/MessageSendModal';

export default function BulkMessages({selectedPracticeChange, fetchChangePracticeData, smsLimit, setSmsLimit}) {
  let location_id = getPractice("location_id");
  let account_id = getPractice("account_id");
  if(selectedPracticeChange != null && fetchChangePracticeData){
    location_id = selectedPracticeChange?.location_id;
    account_id = selectedPracticeChange?.account_id;
  }

  const [messageTemplate] = useState(userMessageTemplates());
  const [dataLoading, setDataLoading] = useState(true);
  const [messageType, setMessageType] = useState([]);
  const [data, setData] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [resendButtonLoader, setResendButtonLoader] = useState(false);
  const [filter, setFilter] = useState({
    page: 1,
    per_page: 10,
    search_term: "",
    message_type: "",
    status: "",
    order_by: [],
  });

  const [dataCount, setDataCount] = useState({
    totalCount: 0,
    totalPages: 0
  });

  const [checkedIds, setCheckedIds] = useState([]);
  const [checkSelected, setCheckSelected] = useState(checkedIds.length > 0);
  const [globalCheckedIds, setGlobalCheckedIds] = useState(new Set());
  const allowRetryStatus = ["Insufficient Credits", "Queued"];

  const loadTemplate = async () => {
    const response = await fetchMessage(
      {
        page: 1,
        per_page: 1000,
        search_term: "",
        message_type: "",
        order_by: [],
      },
      account_id,
      location_id
    );
    if (response) {
      const uniqueMessageTypes = Array.from(
        new Set(response.data.results.map((type) => type.notification_type))
      );
      const uniqueResults = uniqueMessageTypes.map((notification_type) => ({
        notification_type,
      }));

      setMessageType(uniqueResults);
    }
  };

  useEffect(() => {
    loadTemplate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadData(filter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  // Loads data based on the provided filter.
  const loadData = async (getFilter) => {
    setFilter(getFilter);
    let isCanceled = false;

    try {
      const response = await fetchMessage(getFilter, account_id, location_id);
      if (response) {
        const generateData = generateMessageTableData(response.data.results);
        setData(generateData);
        setDataCount({
          totalCount: response.data.count,
          totalPages: response.data.total_pages,
        });
      }
      if (checkedIds.length < 1) {
        setSelectAll(false);
      }
    } catch (error) {
      if (error?.code === "ERR_CANCELED" || error?.name === "CanceledError") {
        isCanceled = true;
      }
    } finally {
      if (!isCanceled) {
        setDataLoading(false);
      }
    }
  };

  // Handles the checkbox change event for a specific message
  const handleCheckboxChange = (id, status) => {
    const selectedStatus = document.querySelector("table > tbody > tr.row_"+id+" > td:nth-child(5)")?.textContent.trim();
    if(!allowRetryStatus.includes(selectedStatus)) {
      return;
    }

    const isChecked = checkedIds.includes(id);
    if (isChecked) {
      setCheckedIds(checkedIds.filter((checkedId) => checkedId !== id));
    } else {
      setCheckedIds([...checkedIds, id]);
    }
    setData((prevData) =>
      prevData.map((row) =>
        row.id === id ? { ...row, checked: !row.checked } : row
      )
    );
    setGlobalCheckedIds((prevCheckedIds) => {
      const updatedCheckedIds = new Set(prevCheckedIds);
      if (updatedCheckedIds.has(id)) {
        updatedCheckedIds.delete(id);
      } else {
        updatedCheckedIds.add(id);
      }
      return updatedCheckedIds;
    });
  };

  // Handles the select all functionality for data.
  const handleSelectAll = (currentPageData) => {
    const allowedRows = currentPageData.filter((data) =>
      allowRetryStatus.includes(data.raw_status)
    );
  
    if (selectAll) {
      // Deselect all allowed rows
      const newCheckedIds = checkedIds.filter(
        (id) => !allowedRows.some((row) => row.id === id)
      );
  
      setCheckedIds(newCheckedIds);
      setGlobalCheckedIds((prevCheckedIds) => {
        const updatedCheckedIds = new Set(prevCheckedIds);
        allowedRows.forEach((row) => updatedCheckedIds.delete(row.id));
        return updatedCheckedIds;
      });
    } else {
      // Select only allowed rows
      const newCheckedIds = Array.from(
        new Set([...checkedIds, ...allowedRows.map((row) => row.id)])
      );
  
      setCheckedIds(newCheckedIds);
      setGlobalCheckedIds((prevCheckedIds) => {
        const updatedCheckedIds = new Set(prevCheckedIds);
        allowedRows.forEach((row) => updatedCheckedIds.add(row.id));
        return updatedCheckedIds;
      });
    }
  
    setSelectAll(!selectAll);
    setData((prevData) =>
      prevData.map((row) => ({
        ...row,
        checked: !selectAll && allowRetryStatus.includes(row.raw_status),
      }))
    );
  };

  // Handles the page change event.
  const handlePageChange = (newPage) => {
    setFilter({ ...filter, page: newPage });
    setDataLoading(true);
  };

  // Handles the change event for the search term input field.
  const debouncedSetFilter = useCallback(
    debounce((newFilter) => setFilter(newFilter), 300), []
  );

  const handleChangeSearchTerm = (event) => {
    const newFilter = { ...filter, search_term: event.target.value, page: 1 };
    setDataLoading(true);
    debouncedSetFilter(newFilter);
  };

  // Handles the change of rows per page in the filter.
  const handleChangeRowsPerPage = (value) => {
    setFilter({
      ...filter,
      page: 1,
      per_page: value,
    });
    setDataLoading(true);
  };

  useEffect(() => {    
    setCheckSelected(checkedIds.length > 0 ? true : false);
  }, [checkedIds]);

  const filterByType = async (e) => {
    setFilter({ ...filter, message_type: e.target.value });
    setDataLoading(true);
  };

  const filterByStatus = async (e) => {
    setFilter({ ...filter, status: e.target.value });
    setDataLoading(true);
  };

  useEffect(() => {
    const sameLocation = filter.location_id === location_id;
    const sameAccount = filter.account_id === account_id;

    if (sameLocation && sameAccount) return;

    setFilter({
      ...filter,
      location_id: location_id,
      account_id: account_id,
    });
    setDataLoading(true);
    setCheckedIds([]);
    setGlobalCheckedIds(new Set());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location_id, account_id]);

  const getSMSLimit = async() => {
    const result = await getSMSLimitData(account_id);
    if (result) {
      setSmsLimit(result?.data);
    }
  }

  const resendMessages = async () => {
    setResendButtonLoader(true);
    
    try {
      const resendDataParams = {
        location_id: location_id,
        account_id: account_id,
        message_transaction_ids: checkedIds,
      };

      const result = await retrySendBulkMessage(resendDataParams);
      
      if (result?.detail) {
        notify("error", result?.detail);
      } else { 
        if(result?.data?.status === 'success'){
          notify("success", result?.data?.message);
          setCheckedIds([]);
          setGlobalCheckedIds(new Set());
          setTimeout(() => {
            getSMSLimit();
          }, 5000);
          loadData(filter);
        } else {
          notify("error", result?.data?.message);
        }
      }
      
    } catch (error) {
      if (error?.response && error?.response?.data && error?.response?.data?.detail) {
        notify("error", error?.response?.data?.detail, 0.5);
      }
    } finally {
      setResendButtonLoader(false);
    }
  }

  useDocumentTitle("Message Transaction");

  return (
    <>
      <div className="docpro_page_heading_wrap">
        <h2 className="docpro_page_heading docpro_fs_24">
          <i
            className="docpro_white_color"
            style={{ backgroundColor: "#898E87" }}
          >
            <MessageSquareShare width={20} height={20} />
          </i>
          Message Transaction
          <span>{numberWithCommas(dataCount.totalCount)}</span>
        </h2>
        <MessageSendModal 
          account_id={account_id} 
          location_id={location_id}
          messageTemplate={messageTemplate}
          smsLimit={smsLimit}
          setSmsLimit={setSmsLimit}
          reloadFilter={filter}
          reloadData={loadData}
        />
      </div>
      <Spacing lg="12" sm="12" />
      <div className="docpro_data_table_wrap">
        <div className="docpro_data_table_headings">
          <div className="docpro_data_table_heading_input_group">
            <form className="docpro_search_form">
              <input
                type="text"
                placeholder="Search"
                className="docpro_search_input"
                onChange={handleChangeSearchTerm}
              />
              <button className="docpro_search_btn">
                <Search width={20} height={20} />
              </button>
            </form>
          </div>
          <div className='flex items-center docpro_grid_gap_10'>
            <select
              className="docpro_form_field"
              onChange={filterByStatus}
              style={{ width: "auto", minWidth: "170px" }}
            >
              <option value="">All</option>
              <option value="Delivered">Delivered</option>
              <option value="Failed">Failed</option>
              <option value="Initialized">Initialized</option>
              <option value="Insufficient Credits">Insufficient Credits</option>
              <option value="Invalid Phone">Invalid Phone</option>
              <option value="Queued">Queued</option>
              <option value="Scheduled">Scheduled</option>
            </select>
            <select
              className="docpro_form_field"
              onChange={filterByType}
              style={{ width: "auto", minWidth: "240px" }}
            >
              <option value="">All</option>
              {messageType?.map((type, index) => (
                <option key={index} value={type.notification_type}>
                  {type.notification_type}
                </option>
              ))}
            </select>
          </div>
        </div>
        <BulkMessageDataTable
          tableData={data}
          handleSelectAll={() => handleSelectAll(data)}
          handleCheckboxChange={handleCheckboxChange}
          selectAll={selectAll}
          checkedIds={checkedIds}
          setCheckedIds={setCheckedIds}
          checkSelected={checkSelected}
          reloadData={loadData}
          reloadDataFilter={filter}
          dataLoading={dataLoading}
          setDataLoading={setDataLoading}
          resendButtonLoader={resendButtonLoader}
          resendMessages={resendMessages}
          globalCheckedIds={globalCheckedIds}
          setGlobalCheckedIds={setGlobalCheckedIds}
        />
        {(data.length > 0 && !dataLoading) && (
          <DataTablePagination
            currentPage={filter.page}
            totalPages={dataCount.totalPages}
            onPageChange={handlePageChange}
            handleChangeRowsPerPage={handleChangeRowsPerPage}
            totalRecords={dataCount.totalCount}
            currentPageData={filter.per_page}
          />
        )}
      </div>
    </>
  );
}